
import type { PropType } from 'vue'
import type { EpbBaseInputSize } from '../epb-base-input'
import type { EpbTextboxShape } from './epb-textbox.types'
import { computed, defineComponent, ref } from 'vue'
import CopyToClipboard from '../copy-to-clipboard.component.vue'
import { EpbBaseInput } from '../epb-base-input'
import { EpbButton } from '../epb-button'
import { EpbInputHelp } from '../epb-input-help'

export default defineComponent({
  name: 'EpbTextbox',
  components: {
    EpbButton,
    EpbInputHelp,
    EpbBaseInput,
    CopyToClipboard,
  },
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    help: {
      type: String,
      default: undefined,
    },
    helpIcon: {
      type: String,
      default: undefined,
    },
    infoIcon: {
      type: String,
      default: 'infocircular',
    },
    label: {
      type: String,
      default: undefined,
    },
    icon: {
      type: String,
      default: undefined,
    },
    type: {
      type: String,
      default: undefined,
    },
    maxlength: {
      type: Number,
      default: undefined,
    },
    min: {
      type: Number,
      default: undefined,
    },
    max: {
      type: Number,
      default: undefined,
    },
    size: {
      type: String as PropType<EpbBaseInputSize>,
      default: 'md',
    },
    shape: {
      type: String as PropType<EpbTextboxShape>,
      default: 'rounded',
    },
    disabled: Boolean,
    background: Boolean,
    required: Boolean,
    copyToClipboard: Boolean,
    loading: Boolean,
    autoRemove: Boolean,
    error: {
      type: String,
      default: undefined,
    },
    info: {
      type: String,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
  },
  emits: {
    input: (value: string | number) => {},
    change: (value: string | number) => {},
    keydown: (event: Event) => {},
    focus: () => {},
    blur: (event: FocusEvent) => {},
  },
  setup(props, { emit }) {
    const baseClass = 'epb-textbox'

    const hover = ref(false)

    const inputRef = ref<HTMLInputElement>()

    const classes = computed((): Array<string | Record<string, boolean>> => {
      return [
        baseClass,
        `${baseClass}--${props.size}`,
        `${baseClass}--${props.shape}`,
        {
          [`${baseClass}--type-${props.type}`]: !!props.type,
          [`${baseClass}--background`]: props.background,
          [`${baseClass}--info`]: !!props.info,
          [`${baseClass}--error`]: !!props.error,
          [`${baseClass}--selected`]: !!props.value,
          [`${baseClass}--hide-copy-to-clipboard`]: !hover.value && props.copyToClipboard,
        },
      ]
    })

    const totalInputIconsDisplayed = computed<number>(() => {
      const iconsDisplayConditions: boolean[] = [
        !!props.info?.length,
        !!props.error?.length,
        props.autoRemove,
        props.copyToClipboard,
        props.loading,
      ]
      return iconsDisplayConditions.filter(Boolean).length
    })

    const changeNumberValue = (stepUp: boolean): void => {
      if (stepUp) {
        inputRef.value.stepUp()
      } else {
        inputRef.value.stepDown()
      }
      emit('input', inputRef.value.value)
    }

    return {
      baseClass,
      hover,
      inputRef,
      classes,
      totalInputIconsDisplayed,
      changeNumberValue,
    }
  },
})
